import { ScrollingModule } from '@angular/cdk/scrolling';
import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectChange } from '@angular/material/select';
import { Store } from '@ngrx/store';
import { FullscreenLayoutAreas } from '@src/assets/css/fullscreen.template.scss';
import { PSTFieldNames } from '@src/suite/common/data-models/fields/pst-field-names';
import { ViewStateSortOders } from '@src/suite/common/enums/viewstate-sort-orders';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { selectParentRecord } from '@src/suite/common/store/fullscreen/selectors/fullscreen.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NotesActions } from '@src/suite/common/store/notes/notes.actions';
import {
    selectNotesFilters,
    selectNotesItemHeights,
    selectNotesSearchTerm,
    selectNotesSort,
    selectNotesSortBy,
    selectNotesSortOrder,
    selectNoteTreeDataSource,
    selectSortByOptions,
    selectTreeControl,
} from '@src/suite/common/store/notes/notes.selectors';
import { selectNotesRecords, selectNoteTemplates } from '@src/suite/common/store/records/records.selectors';
import { adjustFilterDefinition } from '@src/suite/common/utils/filter-utils';
import { I18nKeys } from '@src/suite/common/utils/i18n.default';

import { CheckListComponent } from '../../controls/check-list/check-list';
import { DifferentSizesVirtualScrollDirective } from '../../directives/different-sizes-virtual-scroll';
import { ClickStopPropagationDirective } from '../../directives/stop-propagation';
import { OPTION_BLANK } from '../../enums/constants';
import { ObjectTypes } from '../../enums/object-types';
import { selectFieldDefsDict } from '../../store/global-tables/global-tables.selectors';
import { selectDirectionality } from '../../store/hello-results/hello-results.selectors';
import { PlaybookApiActions } from '../../store/playbook/playbook.actions';
import { NotesRecordActions } from '../../store/records/records.actions';
import { RecordHttpActions } from '../../store/records/records.actions';
import { getTopicSecurityPrivsWrite } from '../../utils/record-utils';
import { NoteItemComponent } from '../note-item/note-item.component';
import { SimpleSearchBoxAutocompleteComponent } from '../search-box/simple-search-box-autocomplete/simple-search-box-autocomplete';

@Component({
    selector: 'st-notes-list-sidecar',
    imports: [
        CheckListComponent,
        ClickStopPropagationDirective,
        DifferentSizesVirtualScrollDirective,
        MatButtonModule,
        MatIcon,
        MatListModule,
        MatMenuModule,
        NoteItemComponent,
        ScrollingModule,
        SimpleSearchBoxAutocompleteComponent,
    ],
    templateUrl: './notes-list-sidecar.component.html',
    styleUrl: './notes-list-sidecar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-notes-list-sidecar',
    },
})
export class NotesListSidecarDialogComponent implements OnInit {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);

    readonly sidecarMenu = input<FullscreenLayoutAreas>();

    notesLabel = this.store.selectSignal(selectTranslatedKey(I18nKeys.PSTWebNotesLabel));
    parentRecord = this.store.selectSignal(selectParentRecord);
    notes = this.store.selectSignal(selectNotesRecords);
    i18n = this.store.selectSignal(selectI18N)();
    treeControl = this.store.selectSignal(selectTreeControl);
    treeDataSource = this.store.selectSignal(selectNoteTreeDataSource);
    noteHeights = this.store.selectSignal(selectNotesItemHeights);
    searchTerm = this.store.selectSignal(selectNotesSearchTerm);
    noteTemplates = this.store.selectSignal(selectNoteTemplates);

    all = this.i18n.PSTWebAllOption;

    readonly expandNotes = signal(true);
    readonly selectedStatus = signal<string>(this.all);

    readonly expandNotesIcon = computed(() => (this.expandNotes() ? 'collapse_all' : 'expand_all'));
    readonly notesFilters = linkedSignal(() => {
        const filter = { ...this.store.selectSignal(selectNotesFilters)() };
        filter.Filters = filter.Filters?.map(definition => ({ ...definition })) || [];
        return filter;
    });

    readonly noteStatusLabels = computed(() => {
        return [this.all, ...Object.values(this.globalTablesService.getListItemsForField(PSTFieldNames.PSTNoteStatus))];
    });

    readonly allFields = computed(() => this.store.selectSignal(selectFieldDefsDict)());
    readonly selectedSort = computed(() => this.store.selectSignal(selectNotesSort)());
    readonly selectedSortOrder = computed(() => this.store.selectSignal(selectNotesSortOrder)());
    readonly selectedSortBy = computed(() => this.store.selectSignal(selectNotesSortBy)());
    readonly selectedSortId = computed(() => {
        return `${this.selectedSortBy()}${this.selectedSortOrder()}`;
    });
    readonly sortOptions = computed(() => this.store.selectSignal(selectSortByOptions)());

    readonly noteSortOptions = computed(() => {
        return (
            this.sortOptions()?.Options.map(o => {
                const keyPrefix = `PSTWebListValue_${PSTFieldNames[o.id]}`;
                const ascId = `${keyPrefix}_${ViewStateSortOders.ASC}`;
                const descId = `${keyPrefix}_${ViewStateSortOders.DESC}`;
                return [
                    {
                        id: descId,
                        label: this.i18n[descId] || `${o.title} ${ViewStateSortOders.DESC}`,
                        metadata: {
                            sortBy: o.id,
                            sortOrder: ViewStateSortOders.DESC,
                        },
                    },
                    {
                        id: ascId,
                        label: this.i18n[ascId] || `${o.title} ${ViewStateSortOders.ASC}`,
                        metadata: {
                            sortBy: o.id,
                            sortOrder: ViewStateSortOders.ASC,
                        },
                    },
                ];
            }) || []
        ).flat();
    });

    readonly statusBtnLabel = computed(() => {
        return this.i18n.PSTWebNoteStatus + ': ' + this.selectedStatus();
    });

    readonly sortBtnLabel = computed(() => {
        return this.i18n[`PSTWebListValue_${this.selectedSortBy()}_${this.selectedSortOrder()}`] || `${this.allFields()[this.selectedSortBy()].DefaultCaption} ${ViewStateSortOders.ASC}`;
    });

    readonly directionality = computed(() => {
        return this.store.selectSignal(selectDirectionality)();
    });

    readonly disabled = computed(() => {
        if (this.parentRecord()?.Type === ObjectTypes.TOPIC) {
            return !getTopicSecurityPrivsWrite(this.parentRecord()) && !this.parentRecord()?.Path?.isWasteBinPath;
        }
        return false;
    });

    readonly topicRecordGuid = computed(() => {
        if (this.parentRecord()?.Type === ObjectTypes.TOPIC) {
            return this.parentRecord()?.Guid;
        }
        return null;
    });

    ngOnInit(): void {
        this.store.dispatch(PlaybookApiActions.loadNoteTemplates());
    }

    createNote() {
        this.store.dispatch(
            NotesActions.initializeNewNote({
                parentRecordGuid: this.topicRecordGuid(),
            })
        );
    }
    noteClicked(noteGuid: string) {
        this.store.dispatch(
            NotesActions.openEditNote({
                record: {
                    Guid: noteGuid,
                },
            })
        );
    }

    onFilterChange() {
        this.store.dispatch(NotesActions.updateNotesFilter({ filter: this.notesFilters() }));
    }

    onQueryTermChange(searchTerm: string) {
        this.store.dispatch(NotesActions.changeSearchTerm({ searchTerm: searchTerm }));
    }

    onSortChange(value: {
        id: string;
        label: string;
        metadata: {
            sortBy: PSTFieldNames;
            sortOrder: ViewStateSortOders;
        };
    }) {
        this.store.dispatch(NotesActions.changeSort({ sort: { sortBy: value.metadata.sortBy, sortOrder: value.metadata.sortOrder } }));
    }

    public onSelectionChange(value: unknown, index: number, definition: StorytellingFilterDefinition) {
        if (value instanceof MatSelectChange) {
            value = value.value;
        }
        definition = adjustFilterDefinition(definition, this.i18n, value);
        this.notesFilters.update(filters => {
            const newFilters = { ...filters };
            newFilters.Filters = filters.Filters.map(def => ({ ...def }));
            newFilters.Filters[index] = definition;
            return newFilters;
        });
    }

    public trackByGuid(_, node) {
        if (node) {
            return node?.Guid;
        }
    }

    toggleExpandAllNotes() {
        this.expandNotes.set(!this.expandNotes());
        this.store.dispatch(NotesRecordActions.changeAllNotesExpandedState({ isExpanded: this.expandNotes() }));
    }

    onNoteTemplateMenuOpened() {
        this.store.dispatch(PlaybookApiActions.loadNoteTemplates());
    }

    deleteTemplate(note: StorytellingNoteRecord) {
        this.store.dispatch(RecordHttpActions.deleteRecordTemplate({ guid: note.Guid, recordType: note.Type }));
    }

    selectNoteTemplate(template: StorytellingNoteRecord) {
        if (!template.isDeleted) {
            this.store.dispatch(
                NotesActions.initializeNewNote({
                    templateRecord: template.Guid === OPTION_BLANK ? null : template,
                    parentRecordGuid: this.topicRecordGuid(),
                })
            );
        }
    }
}
